import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { CartService, CartItem } from 'app/core/cart/cart.service';
import { ApiService } from 'app/shared/api.service';

export interface ProductDetail {
    name: string;
    product_category_name: string;
    short_description: string;
    description: string;
}

export interface ServiceProduct {
    id: string;
    name: string;
    subtitle: string;
    category: string;
    price: number;
    priceFormatted: string;
    originalPrice?: number;
    originalPriceFormatted?: string;
    image: string;
    badge: string;
    badgeColor: string;
    rating: number;
    completedOrders: string;
    description: string;
    longDescription: string;
    features: string[];
    deliverables: string[];
    equipment: string[];
    packages?: { id?: string; name: string; price: number; priceFormatted: string; duration: string }[];
    selectedPackageOption?: any;
    showMore?: boolean;
}

@Component({
    selector: 'app-professional-photographer',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './professional-photographer.component.html',
    styleUrls: ['./professional-photographer.component.scss'],
})
export class ProfessionalPhotographerComponent implements OnInit {
    products: ServiceProduct[] = [];

    productDetail: ProductDetail | null = null;
    phone = '6282260084847';

    constructor(
        private cartService: CartService,
        private router: Router,
        private apiService: ApiService
    ) { }

    ngOnInit(): void {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        this.fetchDynamicProducts();
    }

    async fetchDynamicProducts(): Promise<void> {
        const professionalDocumentationId = '01a0faa9-85e0-72e9-a687-6b97f9cad260';

        try {
            const [productResponse, packageResponse] = await Promise.all([
                this.apiService.get(
                    `https://dev.api.visideastudio.com/api/v1/front/public/non-themed-products/${professionalDocumentationId}`,
                    false
                ),
                this.apiService.get(
                    `https://dev.api.visideastudio.com/api/v1/front/public/non-themed-product-packages/list?paginate=false&non_themed_product_id=${professionalDocumentationId}`,
                    false
                ),
            ]);
            const productDetail = productResponse?.data;
            const packages = packageResponse?.data?.list;

            if (!productDetail || !Array.isArray(packages)) {
                return;
            }

            this.productDetail = {
                name: productDetail.name,
                product_category_name: productDetail.product_category_name,
                short_description: productDetail.short_description,
                description: productDetail.description,
            };

            this.products = packages
                .filter((apiPackage: any) => apiPackage.is_active)
                .map((apiPackage: any) => ({
                    id: apiPackage.id,
                    name: apiPackage.name,
                    subtitle: productDetail.short_description || '',
                    category: apiPackage.non_themed_product_name || productDetail.product_category_name || '',
                    price: apiPackage.price || 0,
                    priceFormatted: this.formatRupiah(apiPackage.price || 0),
                    originalPrice: apiPackage.regular_price || undefined,
                    originalPriceFormatted: apiPackage.regular_price
                        ? this.formatRupiah(apiPackage.regular_price)
                        : undefined,
                    image: apiPackage.image?.original_url || '',
                    badge: '',
                    badgeColor: '',
                    rating: 0,
                    completedOrders: '',
                    description: apiPackage.description || productDetail.short_description || '',
                    longDescription: apiPackage.description || productDetail.description || '',
                    features: (apiPackage.description || '').split(/\r?\n/).filter(Boolean),
                    deliverables: [],
                    equipment: [],
                    packages: (apiPackage.variants || [])
                        .filter((variant: any) => variant.is_active)
                        .map((variant: any) => ({
                            id: variant.id,
                            name: variant.name,
                            duration: variant.description || '',
                            price: variant.price || 0,
                            priceFormatted: this.formatRupiah(variant.price || 0),
                        })),
                    selectedPackageOption: (apiPackage.variants || []).filter((v: any) => v.is_active).length > 0 
                        ? {
                            id: (apiPackage.variants || []).filter((v: any) => v.is_active)[0].id,
                            name: (apiPackage.variants || []).filter((v: any) => v.is_active)[0].name,
                            duration: (apiPackage.variants || []).filter((v: any) => v.is_active)[0].description || '',
                            price: (apiPackage.variants || []).filter((v: any) => v.is_active)[0].price || 0,
                            priceFormatted: this.formatRupiah((apiPackage.variants || []).filter((v: any) => v.is_active)[0].price || 0),
                        }
                        : null,
                    showMore: false,
                }));
        } catch (error) {
            console.error('Failed to load Professional Documentation products', error);
        }
    }

    formatRupiah(num: number): string {
        return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(num).replace(',00', '');
    }

    toggleShowMore(product: ServiceProduct, event?: Event): void {
        if (event) {
            event.stopPropagation();
        }
        const currentStatus = product.showMore;
        this.products.forEach(p => p.showMore = false);
        product.showMore = !currentStatus;
    }

    selectPackage(product: ServiceProduct, pkg: any, event?: Event): void {
        if (event) {
            event.stopPropagation();
        }
        product.selectedPackageOption = pkg;
    }

    addToCart(product: ServiceProduct, event?: Event): void {
        if (event) {
            event.stopPropagation();
        }

        const selectedOption = product.selectedPackageOption;
        const price = selectedOption ? selectedOption.price : product.price;
        const priceFormatted = selectedOption ? selectedOption.priceFormatted : product.priceFormatted;
        const optionTitle = selectedOption ? selectedOption.name : 'Paket Standar';
        const id = selectedOption && selectedOption.id ? selectedOption.id : product.id;

        const cartItem: CartItem = {
            id: id,
            name: `${product.name} (${optionTitle})`,
            category: product.category,
            price: price,
            priceFormatted: priceFormatted,
            qty: 1,
            image: product.image,
            description: product.subtitle,
            selectedOption: optionTitle,
            itemable_type: 'non themed product package',
        };

        this.cartService.addItem(cartItem, 1);
    }

    checkout(product: ServiceProduct, event?: Event): void {
        if (event) {
            event.stopPropagation();
        }

        const selectedPackage = product.selectedPackageOption;
        const optionTitle = selectedPackage?.name || 'Paket Standar';

        void this.cartService.startDirectCheckout({
            id: selectedPackage && selectedPackage.id ? selectedPackage.id : product.id,
            name: `${product.name} (${optionTitle})`,
            category: product.category,
            price: selectedPackage?.price ?? product.price,
            priceFormatted: selectedPackage?.priceFormatted ?? product.priceFormatted,
            image: product.image,
            description: product.subtitle,
            selectedOption: optionTitle,
            itemable_type: 'non themed product package',
        }, 1);
    }

    consultWhatsApp(product?: ServiceProduct): void {
        const productName = product ? product.name : 'Layanan Professional Documentation';
        const msg = `Halo Visidea Studio, saya tertarik untuk konsultasi dan memesan layanan ${productName}. Bisa dibantu informasi jadwal dan ketersediaan tim? Terima kasih!`;
        const url = `https://wa.me/${this.phone}?text=${encodeURIComponent(msg)}`;
        window.open(url, '_blank');
    }
}
